/**
 * Utility functions for dashboard naming conventions
 * Handles copying naming patterns and prevents iterative copies
 */

/**
 * Pattern to match existing copy suffixes like "- Copy", "- Copy 2", etc.
 */
const COPY_PATTERN = /\s*-\s*Copy(?:\s+\d+)?$/;

/**
 * Generates a unique copy name for a dashboard or tab
 * Prevents iterative copying patterns like "Dashboard - Copy - Copy"
 *
 * Rules:
 * - If name doesn't have "- Copy", append " - Copy"
 * - If name already has "- Copy", replace with " - Copy 2", "- Copy 3", etc.
 * - Never creates patterns like "- Copy - Copy"
 * - If existingNames provided, finds the highest copy number to avoid conflicts
 *
 * @param originalName - The original dashboard/tab name
 * @param existingNames - Optional array of existing names to check against
 * @returns The new name with appropriate copy suffix
 */
export const generateDashboardCopyName = (
  originalName: string,
  existingNames?: string[],
): string => {
  const trimmed = originalName.trim();
  const baseName = getBaseNameWithoutCopy(trimmed);

  // If existingNames provided, find the highest copy number
  if (existingNames && existingNames.length > 0) {
    const copyPattern = new RegExp(
      `^${baseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*-\\s*Copy(?:\\s+(\\d+))?$`,
      "i",
    );

    let highestNumber = 0;
    let hasBaseCopy = false;

    for (const name of existingNames) {
      const match = name.match(copyPattern);
      if (match) {
        if (match[1]) {
          const num = parseInt(match[1], 10);
          if (num > highestNumber) {
            highestNumber = num;
          }
        } else {
          hasBaseCopy = true;
        }
      }
    }

    // Generate the next available name
    if (highestNumber > 0) {
      return `${baseName} - Copy ${highestNumber + 1}`;
    } else if (hasBaseCopy) {
      return `${baseName} - Copy 2`;
    } else {
      return `${baseName} - Copy`;
    }
  }

  // Simple increment logic (for backend-handled duplicates)
  if (COPY_PATTERN.test(trimmed)) {
    const copyMatch = trimmed.match(/-\s*Copy\s+(\d+)$/);
    if (copyMatch) {
      const copyNumber = parseInt(copyMatch[1], 10);
      return `${baseName} - Copy ${copyNumber + 1}`;
    } else {
      return `${baseName} - Copy 2`;
    }
  }

  return `${trimmed} - Copy`;
};

/**
 * Checks if a name is in the format we use for copies
 * This helps identify if a dashboard is already a copy
 */
export const isCopyName = (name: string): boolean => {
  return COPY_PATTERN.test(name.trim());
};

/**
 * Extracts the base name without the copy suffix
 */
export const getBaseNameWithoutCopy = (name: string): string => {
  return name.trim().replace(COPY_PATTERN, "").trim();
};

/**
 * Generates a copy name for a tab using the same pattern as dashboards
 *
 * @param originalName - The original tab name
 * @param existingNames - Optional array of existing tab names to check against
 * @returns The new name with appropriate copy suffix
 */
export const generateTabCopyName = (
  originalName: string,
  existingNames?: string[],
): string => {
  return generateDashboardCopyName(originalName, existingNames);
};
